import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mentorConsoleApi } from '../services/api';
import { SCREENS } from '../lib/screenRegistry';
import './OrgAdmin.css';
import './MentorConsole.css';

const MENTOLOGY_LOGO = encodeURI('/Assets/logo/Mentology Logo.png');

interface MentoringNotes {
  noteDate: string;
  notesText: string;
  followUpAgenda: string;
  nextMeetingAt: string;
}

interface MenteeRow {
  menteeToken: string;
  menteeName: string;
  phone: string;
  email: string;
  redAlertStatus: 'NORMAL' | 'CRISIS';
  crisisEventId: number | null;
  crisisType: string | null;
  triggerTerm: string | null;
  aiSnapshot: string;
  mentoringNotes: MentoringNotes;
}

const DEV_LAYOUT_MENTEES: MenteeRow[] = [
  {
    menteeToken: 'ELT-DEV01',
    menteeName: 'Alex Rivera',
    phone: '+61 412 111 001',
    email: 'alex.rivera@demo.org',
    redAlertStatus: 'NORMAL',
    crisisEventId: null,
    crisisType: null,
    triggerTerm: null,
    aiSnapshot: 'Executive digest (20-day window): No AI interaction activity recorded in the past 20 days.',
    mentoringNotes: { noteDate: new Date().toISOString(), notesText: '', followUpAgenda: '', nextMeetingAt: '' },
  },
  {
    menteeToken: 'ELT-DEV02',
    menteeName: 'Jordan Chen',
    phone: '+61 412 111 002',
    email: 'jordan.chen@demo.org',
    redAlertStatus: 'NORMAL',
    crisisEventId: null,
    crisisType: null,
    triggerTerm: null,
    aiSnapshot: 'Executive digest (20-day window): No AI interaction activity recorded in the past 20 days.',
    mentoringNotes: { noteDate: new Date().toISOString(), notesText: '', followUpAgenda: '', nextMeetingAt: '' },
  },
  {
    menteeToken: 'ELT-DEV03',
    menteeName: 'Sam Okafor',
    phone: '+61 412 111 003',
    email: 'sam.okafor@demo.org',
    redAlertStatus: 'NORMAL',
    crisisEventId: null,
    crisisType: null,
    triggerTerm: null,
    aiSnapshot: 'Executive digest (20-day window): No AI interaction activity recorded in the past 20 days.',
    mentoringNotes: { noteDate: new Date().toISOString(), notesText: '', followUpAgenda: '', nextMeetingAt: '' },
  },
];

interface SessionLogEntry {
  date: string;
  notes: string;
}

interface CrisisDetail {
  crisisEventId: number;
  menteeToken: string;
  menteeName: string;
  crisisType: string;
  triggerTerm: string;
  transcript: string;
  systemActions: {
    consoleFrozen: boolean;
    systemMessageInjected: boolean;
    injectedMessage: string;
  };
  calendarSlot: {
    status?: string;
    slot?: {
      title?: string;
      startIso?: string;
      endIso?: string;
      durationMinutes?: number;
      meetLink?: string;
      timezone?: string;
    };
  } | null;
}

function highlightTriggerTerm(transcript: string, term: string | null) {
  if (!term || !transcript) return transcript;
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = transcript.split(new RegExp(`(${escaped})`, 'gi'));
  return parts.map((part, index) => (
    part.toLowerCase() === term.toLowerCase()
      ? <strong key={index} className="mentor-triage-keyword">{part}</strong>
      : part
  ));
}

function formatSlotTime(iso?: string) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

export default function MentorConsole() {
  const navigate = useNavigate();
  const [mentorName, setMentorName] = useState('Human Mentor');
  const [rows, setRows] = useState<MenteeRow[]>([]);
  const [filterToken, setFilterToken] = useState('');
  const [selectedMentee, setSelectedMentee] = useState<MenteeRow | null>(null);
  const [sessionNotes, setSessionNotes] = useState('');
  const [activeTab, setActiveTab] = useState<'notes' | 'history'>('notes');
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, File[]>>({});
  const [notesHistory, setNotesHistory] = useState<Record<string, SessionLogEntry[]>>({});
  const [notesDraft, setNotesDraft] = useState<Record<string, MentoringNotes>>({});
  const [listeningToken, setListeningToken] = useState<string | null>(null);
  type SpeechRecognitionInstance = {
    continuous: boolean;
    interimResults: boolean;
    lang: string;
    start: () => void;
    stop: () => void;
    onresult: ((event: { results: ArrayLike<{ 0: { transcript: string } }> }) => void) | null;
    onend: (() => void) | null;
    onerror: (() => void) | null;
  };
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const canvasSectionRef = useRef<HTMLDivElement | null>(null);
  const [triageOpen, setTriageOpen] = useState(false);
  const [triageDetail, setTriageDetail] = useState<CrisisDetail | null>(null);
  const [triageLoading, setTriageLoading] = useState(false);
  const [deEscalateLoading, setDeEscalateLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  const loadDashboard = useCallback(async () => {
    const data = await mentorConsoleApi.dashboard();
    setMentorName(data.mentor?.name || 'Mentor');
    const mentees: MenteeRow[] = data.mentees || [];
    setRows(mentees);
    const draft: Record<string, MentoringNotes> = {};
    mentees.forEach((row) => {
      draft[row.menteeToken] = { ...row.mentoringNotes };
    });
    setNotesDraft(draft);
  }, []);

  useEffect(() => {
    loadDashboard().catch(() => {});
  }, [loadDashboard]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadDashboard().catch(() => {});
    }, 8000);
    return () => clearInterval(interval);
  }, [loadDashboard]);

  const matrixRows = useMemo(() => {
    if (rows.length > 0) return rows;
    if (import.meta.env.DEV) return DEV_LAYOUT_MENTEES;
    return rows;
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (!filterToken) return matrixRows;
    return matrixRows.filter((row) => row.menteeToken === filterToken);
  }, [matrixRows, filterToken]);

  const resolveActiveMentee = useCallback((): MenteeRow | null => {
    if (filterToken) {
      return matrixRows.find((row) => row.menteeToken === filterToken) ?? null;
    }
    if (selectedMentee) {
      return matrixRows.find((row) => row.menteeToken === selectedMentee.menteeToken) ?? selectedMentee;
    }
    if (filteredRows.length === 1) return filteredRows[0];
    return null;
  }, [filterToken, matrixRows, selectedMentee, filteredRows]);

  const handleFilterChange = (token: string) => {
    setFilterToken(token);
    setActionError('');
    if (!token) return;
    const row = matrixRows.find((item) => item.menteeToken === token);
    if (!row) return;
    if (selectedMentee?.menteeToken !== row.menteeToken) {
      setSessionNotes('');
    }
    setSelectedMentee(row);
  };

  const handleTabSelect = (tab: 'notes' | 'history') => {
    const mentee = resolveActiveMentee();
    if (!mentee) {
      setActionError('Select a mentee from the dropdown to open their workspace.');
      return;
    }
    setActionError('');
    if (selectedMentee?.menteeToken !== mentee.menteeToken) {
      setSessionNotes('');
    }
    setSelectedMentee(mentee);
    setActiveTab(tab);
    window.requestAnimationFrame(() => {
      canvasSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  const openTriage = async (crisisEventId: number) => {
    setTriageLoading(true);
    setActionError('');
    try {
      const detail = await mentorConsoleApi.crisisDetail(crisisEventId);
      setTriageDetail(detail);
      setTriageOpen(true);
    } catch (err: unknown) {
      setActionError((err as { error?: string }).error || 'Unable to load crisis triage data.');
    } finally {
      setTriageLoading(false);
    }
  };

  const handleDeEscalate = async () => {
    if (!triageDetail) return;
    setDeEscalateLoading(true);
    setActionError('');
    try {
      await mentorConsoleApi.deEscalate(triageDetail.crisisEventId);
      setTriageOpen(false);
      setTriageDetail(null);
      await loadDashboard();
    } catch (err: unknown) {
      setActionError((err as { error?: string }).error || 'De-escalation failed.');
    } finally {
      setDeEscalateLoading(false);
    }
  };

  const handleSaveNotes = async (menteeToken: string, notesText?: string) => {
    const draft = notesDraft[menteeToken] || rows.find((r) => r.menteeToken === menteeToken)?.mentoringNotes;
    const text = notesText ?? draft?.notesText ?? '';
    if (!draft && !notesText) return;
    try {
      await mentorConsoleApi.saveNotes({
        menteeToken,
        notesText: text,
        followUpAgenda: draft?.followUpAgenda ?? '',
        nextMeetingAt: draft?.nextMeetingAt ?? '',
      });
      if (text.trim()) {
        const entry: SessionLogEntry = {
          date: new Date().toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' }),
          notes: text.trim(),
        };
        setNotesHistory((prev) => ({
          ...prev,
          [menteeToken]: [entry, ...(prev[menteeToken] || [])],
        }));
      }
      setNotesDraft((prev) => ({
        ...prev,
        [menteeToken]: {
          ...(prev[menteeToken] || { followUpAgenda: '', nextMeetingAt: '', noteDate: new Date().toISOString() }),
          notesText: text,
          noteDate: new Date().toISOString(),
        },
      }));
      await loadDashboard();
    } catch (err: unknown) {
      setActionError((err as { error?: string }).error || 'Notes save failed.');
    }
  };

  const openMenteeWorkspace = (row: MenteeRow) => {
    setFilterToken(row.menteeToken);
    setSelectedMentee(row);
    setSessionNotes('');
    setActiveTab('notes');
    setActionError('');
    window.requestAnimationFrame(() => {
      canvasSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  const handleFileUpload = (menteeToken: string, file: File | undefined) => {
    if (!file) return;
    setUploadedFiles((prev) => ({
      ...prev,
      [menteeToken]: [...(prev[menteeToken] || []), file],
    }));
  };

  const attachedFilesLabel = (menteeToken: string) => {
    const files = uploadedFiles[menteeToken] || [];
    if (files.length === 0) return 'No custom context files attached';
    return files.map((file) => file.name).join(', ');
  };

  const handleWorkspaceSaveNotes = async () => {
    if (!selectedMentee) return;
    const menteeToken = selectedMentee.menteeToken;
    updateNotesField(menteeToken, 'notesText', sessionNotes);
    await handleSaveNotes(menteeToken, sessionNotes);
    setSessionNotes('');
  };

  const updateNotesField = (menteeToken: string, field: keyof MentoringNotes, value: string) => {
    setNotesDraft((prev) => ({
      ...prev,
      [menteeToken]: {
        ...prev[menteeToken],
        [field]: value,
      },
    }));
    if (selectedMentee?.menteeToken === menteeToken && field === 'notesText') {
      setSessionNotes(value);
    }
  };

  const toggleSpeechToText = () => {
    if (!selectedMentee) return;
    const menteeToken = selectedMentee.menteeToken;
    const win = window as Window & {
      SpeechRecognition?: new () => SpeechRecognitionInstance;
      webkitSpeechRecognition?: new () => SpeechRecognitionInstance;
    };
    const Ctor = win.SpeechRecognition || win.webkitSpeechRecognition;
    if (!Ctor) {
      alert('Speech-to-text is not supported in this browser.');
      return;
    }

    if (listeningToken === menteeToken && recognitionRef.current) {
      recognitionRef.current.stop();
      setListeningToken(null);
      return;
    }

    const recognition = new Ctor();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-AU';
    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((r) => r[0].transcript)
        .join('');
      setSessionNotes((current) => {
        const next = `${current} ${transcript}`.trim();
        updateNotesField(menteeToken, 'notesText', next);
        return next;
      });
    };
    recognition.onend = () => setListeningToken(null);
    recognition.onerror = () => setListeningToken(null);
    recognitionRef.current = recognition;
    recognition.start();
    setListeningToken(menteeToken);
  };

  const handleEmailClick = (email: string) => {
    if (!email?.trim()) return;
    window.location.href = `mailto:${encodeURIComponent(email.trim())}`;
  };

  const isMenteeAlert = (row: MenteeRow) => row.redAlertStatus === 'CRISIS';

  const handleRemoveMentee = async (row: MenteeRow) => {
    const first = window.confirm(
      `Remove ${row.menteeName} from your mentor tracking matrix? This will release their license seat.`,
    );
    if (!first) return;
    const second = window.confirm(
      'FINAL CONFIRMATION: This action cannot be undone from this console. Proceed with mentee removal?',
    );
    if (!second) return;

    try {
      await mentorConsoleApi.removeMentee(row.menteeToken);
      if (filterToken === row.menteeToken) setFilterToken('');
      if (selectedMentee?.menteeToken === row.menteeToken) setSelectedMentee(null);
      await loadDashboard();
    } catch (err: unknown) {
      setActionError((err as { error?: string }).error || 'Mentee removal failed.');
    }
  };

  const handleConfirmCalendar = async () => {
    if (!triageDetail) return;
    try {
      const result = await mentorConsoleApi.confirmCalendar(triageDetail.crisisEventId);
      setTriageDetail((prev) => (prev ? { ...prev, calendarSlot: result.calendarSlot } : prev));
    } catch (err: unknown) {
      setActionError((err as { error?: string }).error || 'Calendar confirmation failed.');
    }
  };

  const handleRescheduleCalendar = async () => {
    if (!triageDetail) return;
    const input = window.prompt('Enter new start time (ISO format or leave blank for +30 min):');
    const newStart = input?.trim() || new Date(Date.now() + 30 * 60 * 1000).toISOString();
    try {
      const result = await mentorConsoleApi.rescheduleCalendar(triageDetail.crisisEventId, newStart);
      setTriageDetail((prev) => (prev ? { ...prev, calendarSlot: result.calendarSlot } : prev));
    } catch (err: unknown) {
      setActionError((err as { error?: string }).error || 'Calendar reschedule failed.');
    }
  };

  const workspaceHistory = useMemo(() => {
    if (!selectedMentee) return [];
    const token = selectedMentee.menteeToken;
    const saved = notesHistory[token] || [];
    const current = notesDraft[token]?.notesText?.trim();
    if (current && saved.length === 0) {
      const noteDate = notesDraft[token]?.noteDate || selectedMentee.mentoringNotes.noteDate;
      return [{ date: formatSlotTime(noteDate), notes: current }];
    }
    return saved;
  }, [selectedMentee, notesHistory, notesDraft]);

  return (
    <div className="admin-layout mentor-console-layout" data-screen-id={SCREENS.ELT08}>
      <aside className="admin-sidebar mentor-console-sidebar">
        <img
          src={MENTOLOGY_LOGO}
          alt="Mentology"
          className="org-sidebar-logo brand-logo"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
        <p className="org-sidebar-heading">Human Mentor Console</p>
        <label className="mentor-console-filter-label">
          <span className="mentor-console-filter-sr">Filter mentees</span>
          <select
            className="mentor-console-filter-select mentor-assign-select enrollment-field-input enrollment-field-select"
            value={filterToken}
            onChange={(e) => handleFilterChange(e.target.value)}
            aria-label="All Active Mentees"
          >
            <option value="">All Active Mentees</option>
            {matrixRows.map((row) => (
              <option key={row.menteeToken} value={row.menteeToken}>
                {row.menteeName}
              </option>
            ))}
          </select>
        </label>
        <div className="mentor-console-tab-nav">
          <button
            type="button"
            onClick={() => handleTabSelect('notes')}
            className={`mentor-console-tab-btn ${activeTab === 'notes' ? 'mentor-console-tab-btn-active' : 'mentor-console-tab-btn-inactive'}`}
          >
            Entry: Session Notes
          </button>
          <button
            type="button"
            onClick={() => handleTabSelect('history')}
            className={`mentor-console-tab-btn ${activeTab === 'history' ? 'mentor-console-tab-btn-active' : 'mentor-console-tab-btn-inactive'}`}
          >
            Review: Previous Session Logs
          </button>
        </div>
        <div className="mentor-console-mentor-meta">
          <p><strong>Mentor:</strong> {mentorName}</p>
          <p><strong>Active caseload:</strong> {matrixRows.length}</p>
          <button type="button" className="admin-nav-item logout" onClick={() => navigate('/')}>
            Exit Console
          </button>
        </div>
      </aside>

      <main className="admin-panel mentor-console-panel">
        <div className="org-panel-card enrollment-workspace mentor-console-workspace">
          <h2 className="section-header overview-section-heading mentor-console-page-heading">Human Mentor Console</h2>
          {actionError && <p className="error-text enrollment-form-error">{actionError}</p>}

          <div className="mentor-console-main-canvas">
            {filteredRows.length === 0 ? (
              <p className="mentor-directory-empty">No mentees assigned to this mentor.</p>
            ) : (
              filteredRows.map((row) => (
                <div
                  key={row.menteeToken}
                  className={`mentor-console-mentee-stack ${selectedMentee?.menteeToken === row.menteeToken ? 'mentor-console-mentee-stack-active' : ''}`}
                >
                  <div className="mentor-console-mentee-meta-bar">
                    <span className="mentor-console-mentee-meta-name">{row.menteeName}</span>
                    <span className="mentor-console-mentee-meta-phone">{row.phone}</span>
                    <span
                      className={`mentor-console-status-pill ${isMenteeAlert(row) ? 'mentor-console-status-pill-alert' : 'mentor-console-status-pill-fine'}`}
                    >
                      {isMenteeAlert(row) ? 'Needs Attention' : 'Doing Fine'}
                    </span>
                    {isMenteeAlert(row) && row.crisisEventId && (
                      <button
                        type="button"
                        className="mentor-red-crisis"
                        disabled={triageLoading}
                        onClick={() => openTriage(row.crisisEventId!)}
                      >
                        CRISIS INTERCEPT
                      </button>
                    )}
                    {row.email ? (
                      <button
                        type="button"
                        className="mentor-console-email-btn"
                        onClick={() => handleEmailClick(row.email)}
                      >
                        {row.email}
                      </button>
                    ) : (
                      <span className="mentor-console-mentee-meta-muted">—</span>
                    )}
                    <button
                      type="button"
                      className="mentor-console-remove-inline"
                      onClick={() => handleRemoveMentee(row)}
                    >
                      Remove
                    </button>
                  </div>

                  <div className="mentor-console-info-grid">
                    <table className="mentor-console-info-table">
                      <thead>
                        <tr>
                          <th>AI Interaction Snapshot</th>
                          <th aria-hidden="true" />
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <div className="mentor-console-info-snapshot">{row.aiSnapshot}</div>
                          </td>
                          <td className="mentor-console-info-action">
                            <button
                              type="button"
                              className="mentor-view-notes-btn mentor-view-notes-btn-primary"
                              onClick={() => openMenteeWorkspace(row)}
                            >
                              View Notes
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ))
            )}

            <div className="mentor-console-canvas-section" ref={canvasSectionRef}>
              {selectedMentee ? (
                activeTab === 'notes' ? (
                  <div className="mentor-console-notes-panel">
                    <div className="mentor-console-notes-panel-header">
                      <h3 className="mentor-workspace-title">
                        {selectedMentee.menteeName} — Mentoring Workspace
                      </h3>
                      <label className="mentor-workspace-upload-btn mentor-workspace-upload-btn-compact">
                        Upload Industry Context
                        <input
                          type="file"
                          className="mentor-notes-upload-input"
                          onChange={(e) => handleFileUpload(selectedMentee.menteeToken, e.target.files?.[0])}
                        />
                      </label>
                    </div>
                    <span className="mentor-workspace-upload-hint mentor-workspace-upload-hint-inline">
                      {attachedFilesLabel(selectedMentee.menteeToken)}
                    </span>
                    <span className="mentor-workspace-notes-date">
                      {new Date().toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })}
                    </span>
                    <textarea
                      className="input-field enrollment-field-input mentor-workspace-textarea mentor-console-notes-textarea"
                      placeholder="Type or dictate comprehensive session notes here..."
                      value={sessionNotes}
                      onChange={(e) => {
                        setSessionNotes(e.target.value);
                        updateNotesField(selectedMentee.menteeToken, 'notesText', e.target.value);
                      }}
                      rows={9}
                    />
                    <div className="mentor-workspace-notes-actions">
                      <button
                        type="button"
                        className={`mentor-notes-mic ${listeningToken === selectedMentee.menteeToken ? 'mentor-notes-mic-active' : ''}`}
                        onClick={toggleSpeechToText}
                      >
                        Speech-to-Text
                      </button>
                      <button
                        type="button"
                        className="mentor-workspace-save-btn"
                        onClick={handleWorkspaceSaveNotes}
                      >
                        Save Notes
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mentor-console-history-panel">
                    <h3 className="mentor-workspace-title">Previous Session Logs</h3>
                    {workspaceHistory.length > 0 ? (
                      <div className="mentor-workspace-history-scroll mentor-workspace-history-full">
                        {workspaceHistory.map((log, index) => (
                          <article key={`${log.date}-${index}`} className="mentor-workspace-log-card">
                            <div className="mentor-workspace-log-date">{log.date}</div>
                            <p className="mentor-workspace-log-text">{log.notes}</p>
                          </article>
                        ))}
                      </div>
                    ) : (
                      <p className="mentor-workspace-history-empty">
                        No historical notes recorded for this client.
                      </p>
                    )}
                  </div>
                )
              ) : (
                <div className="mentor-workspace-empty">
                  Select a mentee from the dropdown, then choose Entry or Review to open their workspace.
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {triageOpen && triageDetail && (
        <div className="mentor-triage-overlay" role="dialog" aria-modal="true">
          <div className="mentor-triage-panel">
            <header className="mentor-triage-header">
              <h2 className="mentor-triage-title">
                RED ALERT — CRISIS TRIAGE WORKSPACE — {triageDetail.menteeName}
              </h2>
              <button type="button" className="mentor-triage-close" onClick={() => setTriageOpen(false)}>×</button>
            </header>

            <div className="mentor-triage-panes">
              <section className="mentor-triage-pane">
                <h3 className="mentor-triage-pane-title">Panel A — Intercepted Payload</h3>
                <div className="mentor-triage-transcript">
                  {highlightTriggerTerm(triageDetail.transcript, triageDetail.triggerTerm)}
                </div>
              </section>

              <section className="mentor-triage-pane">
                <h3 className="mentor-triage-pane-title">Panel B — System Actions Enforced</h3>
                <ul className="mentor-triage-status-list">
                  <li>
                    Student message box:{' '}
                    <span className="mentor-triage-status-ok">
                      {triageDetail.systemActions.consoleFrozen ? 'FROZEN' : 'ACTIVE'}
                    </span>
                  </li>
                  <li>
                    Mandatory notification injected:{' '}
                    <span className="mentor-triage-status-ok">
                      {triageDetail.systemActions.systemMessageInjected ? 'YES' : 'NO'}
                    </span>
                  </li>
                  <li>
                    Terminal message: &ldquo;{triageDetail.systemActions.injectedMessage}&rdquo;
                  </li>
                  <li>Crisis type: <strong>{triageDetail.crisisType}</strong></li>
                  <li>Trigger term: <strong className="mentor-triage-keyword">{triageDetail.triggerTerm}</strong></li>
                </ul>
              </section>

              <section className="mentor-triage-pane">
                <h3 className="mentor-triage-pane-title">Panel C — Google Calendar Handshake</h3>
                <div className="mentor-triage-calendar">
                  {triageDetail.calendarSlot?.slot ? (
                    <>
                      <p><strong>Status:</strong> {triageDetail.calendarSlot.status || 'booked'}</p>
                      <p><strong>Title:</strong> {triageDetail.calendarSlot.slot.title}</p>
                      <p><strong>Start:</strong> {formatSlotTime(triageDetail.calendarSlot.slot.startIso)}</p>
                      <p><strong>End:</strong> {formatSlotTime(triageDetail.calendarSlot.slot.endIso)}</p>
                      <p><strong>Duration:</strong> {triageDetail.calendarSlot.slot.durationMinutes} minutes</p>
                      <p><strong>Timezone:</strong> {triageDetail.calendarSlot.slot.timezone}</p>
                      {triageDetail.calendarSlot.slot.meetLink && (
                        <p>
                          <a href={triageDetail.calendarSlot.slot.meetLink} target="_blank" rel="noreferrer">
                            Join emergency review
                          </a>
                        </p>
                      )}
                    </>
                  ) : (
                    <p>Calendar slot pending handshake...</p>
                  )}
                </div>
                <div className="mentor-triage-calendar-actions">
                  <button type="button" className="mentor-triage-calendar-btn" onClick={handleConfirmCalendar}>
                    Confirm Slot
                  </button>
                  <button type="button" className="mentor-triage-calendar-btn" onClick={handleRescheduleCalendar}>
                    Reschedule
                  </button>
                </div>
              </section>
            </div>

            <footer className="mentor-triage-footer">
              <button
                type="button"
                className="mentor-deescalate-btn"
                disabled={deEscalateLoading}
                onClick={handleDeEscalate}
              >
                {deEscalateLoading ? 'De-escalating...' : 'DE-ESCALATE / UNFREEZE CONSOLE'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
